import { converObjectErrorInString, convertObjectToCamelCase } from '@/helpers'

import { QUERIES_ADMIN } from './api.constants'
import { api } from './api.service'

import type {
  ShipmentInvoiceProps,
  ShipmentInvoiceWithPagesProps,
} from '@/types'

import { useMutation, useQuery } from '@tanstack/react-query'

import {
  BFF_SHIPMENT_INVOICE_MANAGEMENT_URL,
  BFF_SHIPMENT_INVOICE_URL,
  BFF_SHIPMENT_INVOICES_MANAGEMENT_URL,
  BFF_SHIPMENT_INVOICES_URL
} from './api.urls'

export const useShipmentInvoiceList = (
  params: Record<string, string | number>,
  isEnabled = true,
) => {
  const getShipmentInvoiceList = async (): Promise<{
    success: boolean
    message: string
    data?: ShipmentInvoiceWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key] && copyParams?.[key] !== 'undefined') {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(BFF_SHIPMENT_INVOICES_URL, {
        params: {
          ...(formattedParams as Record<string, string>),
          offset,
        },
      })

      return {
        success: true,
        message: 'Shipment Invoice fetched successfully!',
        data: convertObjectToCamelCase(
          data,
        ) as ShipmentInvoiceWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get Shipment Invoice!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.BFF_SHIPMENT_INVOICE.module, params],
    queryFn: () => getShipmentInvoiceList(),
    enabled: isEnabled,
  })
}

export const useShipmentInvoiceDetail = (uuid?: string) => {
  const getShipmentInvoiceDetail = async (): Promise<{
    success: boolean
    message: string
    data?: ShipmentInvoiceProps
  }> => {
    try {
      const { data } = await api.get(BFF_SHIPMENT_INVOICE_URL(`${uuid}`))

      return {
        success: true,
        message: 'Shipment invoice fetched successfully!',
        data: convertObjectToCamelCase(data) as ShipmentInvoiceProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get Shipment invoice!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.BFF_SHIPMENT_INVOICE.module, uuid],
    queryFn: () => getShipmentInvoiceDetail(),
    enabled: !!uuid,
  })
}

export const useShipmentInvoiceCreate = () => {
  const ShipmentInvoiceCreate = async (
    payload: FormData,
  ): Promise<{
    success: boolean
    message: string
    data?: ShipmentInvoiceProps
  }> => {
    try {
      const { data } = await api.post(
        BFF_SHIPMENT_INVOICES_MANAGEMENT_URL,
        payload,
      )

      return {
        success: true,
        message: 'successfully!',
        data: convertObjectToCamelCase(data) as ShipmentInvoiceProps,
      }
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }
      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_SHIPMENT_INVOICE.module],
    mutationFn: (payload: FormData) =>
      ShipmentInvoiceCreate(payload),
  })
}

export const useShipmentInvoiceEdit = (uuid: string) => {
  const ShipmentInvoiceEdit = async (
    payload: FormData,
  ): Promise<{
    success: boolean
    message: string
    data?: ShipmentInvoiceProps
  }> => {
    try {
      const { data } = await api.patch(
        BFF_SHIPMENT_INVOICE_MANAGEMENT_URL(`${uuid}`),
        payload,
      )

      return {
        success: true,
        message: 'successfully!',
        data: convertObjectToCamelCase(data) as ShipmentInvoiceProps,
      }
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }
      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_SHIPMENT_INVOICE.module],
    mutationFn: (payload: FormData) =>
      ShipmentInvoiceEdit(payload),
  })
}
